import { ButtonBase, Divider, Typography } from "@mui/material";
import { ReactComponent as AndroidLogo } from "assets/android-play-store.svg";
import FuelRewardsComplete from "assets/fuel-rewards-complete.png";
import { ReactComponent as IOSLogo } from "assets/ios-app-store.svg";
import { AddressType, CustomerFacingProductName } from "domain/core/types";
import { formatAddress } from "lib/util";
import React from "react";
import { styled } from "styled-components";
import {
  AppContainer,
  CompletionCardContainer,
  CompletionImage,
  CompletionNextStepsCard,
  CompletionNextStepsDetails,
  CompletionNextStepsDigit,
  CompletionRow,
  ContentWrapper,
} from "ui/components/StyledComponents";
import { PADDING } from "ui/theme";

const SButtonBase = styled(ButtonBase)`
  margin-bottom: 1rem;
`;

interface FuelCompletionProps {
  preferredName: string;
  selectedProduct: string;
  address: AddressType;
  isSwitching: boolean;
}

export const FuelRewardsCompletion: React.FC<FuelCompletionProps> = ({
  preferredName,
  selectedProduct,
  address,
  isSwitching,
}) => {
  const handleIOSPress = (event: React.MouseEvent) => {
    event.preventDefault();
    window.location.href =
      "https://apps.apple.com/nz/app/z-coffee-card/id594734342?ls=1";
  };

  const handleAndroidPress = (event: React.MouseEvent) => {
    event.preventDefault();
    window.location.href =
      "https://play.google.com/store/apps/details?id=nz.co.zenergy.loyaltycard.android";
  };

  return (
    <>
      <ContentWrapper>
        <Typography
          variant="body1"
          sx={{
            paddingBottom: PADDING.small,
            paddingTop: PADDING.large,
          }}
        >
          Kia ora {preferredName},
        </Typography>
        <Typography display="inline" variant="body1">
          Your switch to Z's {CustomerFacingProductName[selectedProduct]} plan
          at{" "}
        </Typography>
        <Typography display="inline" variant="body2">
          {formatAddress(address)}{" "}
        </Typography>
        <Typography display="inline" variant="body1">
          is underway!
        </Typography>
        {isSwitching ? (
          <>
            <Typography variant="body1">
              It can take up to 5 working days to leave your old power company
              (sorry!), but we'll let you know once it's all sorted. Don't worry
              though - Electricity will always stay connected to{" "}
              {formatAddress(address)}.
            </Typography>
          </>
        ) : (
          <Typography variant="body1">
            If we have any questions, we'll get in touch.
          </Typography>
        )}
      </ContentWrapper>

      <CompletionCardContainer>
        <CompletionNextStepsCard key={"card-next-steps-switching-in"}>
          <CompletionNextStepsDigit>
            <Typography variant="h3" component="p">
              1.
            </Typography>
          </CompletionNextStepsDigit>
          <CompletionNextStepsDetails>
            <Typography variant="h3">Switching in</Typography>
            There's nothing else you need to handle before switching in. We'll
            handle switching you over from your previous provider - typically
            within 5 days.
          </CompletionNextStepsDetails>
        </CompletionNextStepsCard>
        <CompletionNextStepsCard key={"card-next-z-app"}>
          <CompletionNextStepsDigit>
            <Typography variant="h3" component="p">
              2.
            </Typography>
          </CompletionNextStepsDigit>
          <CompletionNextStepsDetails>
            <Typography variant="h3">Get the Z App</Typography>
            If you don't already have the Z App you'll need to download it and
            sign up for an account. Make sure you use the same phone number you
            used here - this will enable us to connect your account and load the
            fuel you earn to your Sharetank.
          </CompletionNextStepsDetails>
        </CompletionNextStepsCard>
        <CompletionNextStepsCard key={"card-next-steps-sharetank"}>
          <CompletionNextStepsDigit>
            <Typography variant="h3" component="p">
              3.
            </Typography>
          </CompletionNextStepsDigit>
          <CompletionNextStepsDetails>
            <Typography variant="h3">Check Sharetank</Typography>
            <Typography>
              Your joining litres should arrive in your account 2 weeks after
              your power is switched. Each time you pay your power bill, we'll
              deposit all the fuel you've earned into your Sharetank.
            </Typography>
          </CompletionNextStepsDetails>
        </CompletionNextStepsCard>
        <CompletionNextStepsCard key={"card-next-fill-your-tank"}>
          <CompletionNextStepsDigit>
            <Typography variant="h3" component="p">
              4.
            </Typography>
          </CompletionNextStepsDigit>
          <CompletionNextStepsDetails>
            <Typography variant="h3">Fill your tank</Typography>
            <Typography>
              Visit any Z station nationwide to redeem your Sharetank liters.
              Simply use your Z App to pay with Sharetank. Don't worry if it
              takes you a while to get to a station - your liters do not expire.
            </Typography>
          </CompletionNextStepsDetails>
        </CompletionNextStepsCard>
      </CompletionCardContainer>
      <Typography
        variant="body1"
        sx={{
          paddingBottom: PADDING.xLarge,
        }}
      >
        If you need to get in touch with our support centre we can identify your
        account by your name and phone number. You can now close this browser
        page.
      </Typography>
      <Divider />

      <CompletionRow>
        <CompletionImage
          srcSet={`${FuelRewardsComplete}`}
          src={FuelRewardsComplete}
          alt="Sharetank"
        />
        <AppContainer>
          <Typography variant="h3" color="secondary">
            Get fuel back with Sharetank
          </Typography>
          <Typography>
            Sharetank is a virtual tank that lets your pre-buy for tomorrow's
            fuel at today's prices. You can choose to share your virtual tank
            with friends and whānau. When you receive fuel back with your power
            plan, we will add these litres of fuel into your Sharetank for you
            to use.
          </Typography>
          <Typography>
            Z App is available in either iOS or Android app stores. Once you've
            downloaded, you can create a Sharetank for your fuel rewards.
          </Typography>
          <div>
            <SButtonBase onClick={handleIOSPress} id="button-ios-store">
              <IOSLogo width={200} height={50} preserveAspectRatio="xMinYMin" />
            </SButtonBase>
            <SButtonBase onClick={handleAndroidPress} id="button-android-store">
              <AndroidLogo
                width={200}
                height={50}
                preserveAspectRatio="xMinYMin"
              />
            </SButtonBase>
          </div>
        </AppContainer>
      </CompletionRow>
    </>
  );
};
